import type { FC } from "react";

import { Fieldset } from "@/ui/components/form/Fieldset";
import { InputField } from "@/ui/components/form/Input/InputField";
import { Flex } from "@/ui/components/layout/Flex";
import { Inline } from "@/ui/components/layout/Inline";
import {
  COUNTER_INPUT_ARIA_LABELS,
  COUNTER_INPUT_DEFAULTS,
  COUNTER_INPUT_FIELD_CLASS,
  COUNTER_INPUT_VARIANT_CLASS_MAP,
  type TCounterInputProps,
} from "@/ui/components/form/CounterInput/CounterInput.config";
import {
  useCounterInput,
} from "@/ui/components/form/CounterInput/useCounterInput";
import { IconButton } from "@/ui/components/primitives/IconButton";
import { TextBody } from "@/ui/components/typography/TextBody";
import { cn } from "@/ui/lib/utils";

export const CounterInput: FC<TCounterInputProps> = ({
  label,
  hideLabel,
  variant = COUNTER_INPUT_DEFAULTS.variant,
  displayValue,
  suffix,
  value,
  onChange,
  min = COUNTER_INPUT_DEFAULTS.min,
  max,
  step = COUNTER_INPUT_DEFAULTS.step,
  disabled = false,
  className,
  ...props
}) => {
  const { decrementDisabled, incrementDisabled, decrement, increment } =
    useCounterInput({
      value,
      min,
      max,
      step,
      disabled,
      onChange,
    });
  const resolvedDisplayValue = displayValue ?? value;
  const formattedDisplayValue = suffix
    ? `${resolvedDisplayValue} ${suffix}`
    : resolvedDisplayValue;
  const displayType = suffix || typeof displayValue === "string"
    ? "text"
    : "number";

  return (
    <Fieldset
      data-component="CounterInput"
      label={label}
      hideLabel={hideLabel}
      disabled={disabled}
    >
      <Inline
        {...props}
        gap="2"
        className={cn(
          COUNTER_INPUT_VARIANT_CLASS_MAP[variant].inline,
          className,
        )}
      >
        <IconButton
          aria-label={COUNTER_INPUT_ARIA_LABELS.decrement}
          icon="minus"
          disabled={decrementDisabled}
          className={cn(
            "shrink-0 [&_svg]:size-4",
            COUNTER_INPUT_VARIANT_CLASS_MAP[variant].button,
          )}
          onClick={decrement}
        />

        <Flex
          className={cn(
            "h-9",
            COUNTER_INPUT_VARIANT_CLASS_MAP[variant].inputContainer,
          )}
        >
          <InputField
            aria-label={COUNTER_INPUT_ARIA_LABELS.quantity}
            type={displayType}
            value={formattedDisplayValue}
            min={min}
            max={max}
            step={step}
            readOnly
            disabled={disabled}
            className={cn(
              COUNTER_INPUT_FIELD_CLASS,
              COUNTER_INPUT_VARIANT_CLASS_MAP[variant].input,
            )}
          />
        </Flex>

        <IconButton
          aria-label={COUNTER_INPUT_ARIA_LABELS.increment}
          icon="plus"
          disabled={incrementDisabled}
          className={cn(
            "shrink-0 [&_svg]:size-4",
            COUNTER_INPUT_VARIANT_CLASS_MAP[variant].button,
          )}
          onClick={increment}
        />

        <TextBody
          tag="span"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          { COUNTER_INPUT_ARIA_LABELS.quantity }: { formattedDisplayValue }
        </TextBody>
      </Inline>
    </Fieldset>
  );
};

CounterInput.displayName = "CounterInput";
